import * as XLSX from 'xlsx';

export interface EventData {
  category?: string;
  sub_category?: string;
  event_name: string;
  start_date: string;
  end_date: string;
  location?: string;
  state: string;
  campaign_hook?: string;
  description?: string;
  source?: string;
}

/**
 * Finds a value from a row object using flexible key matching
 */
const findValueByKeys = (row: any, possibleKeys: string[]): any => {
  for (const key of Object.keys(row)) {
    const normalizedKey = key.toLowerCase().trim().replace(/[_\s-]+/g, '');
    
    for (const possibleKey of possibleKeys) {
      const normalizedPossible = possibleKey.toLowerCase().trim().replace(/[_\s-]+/g, '');
      
      if (normalizedKey === normalizedPossible || normalizedKey.includes(normalizedPossible) || normalizedPossible.includes(normalizedKey)) {
        return row[key];
      }
    }
  }
  return '';
};

/**
 * Parses an Excel file and converts it to the required JSON format
 */
export const parseExcelFile = (file: File): Promise<EventData[]> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = e.target?.result;
        const workbook = XLSX.read(data, { type: 'binary' });
        
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { raw: false });
        
        if (jsonData.length === 0) {
          reject(new Error('Excel file is empty or has no data rows'));
          return;
        }
        
        const events: EventData[] = jsonData.map((row: any) => {
          const stateValue = findValueByKeys(row, ['state', 'province', 'region']) || 'ALL';

          const event: any = {
            event_name: findValueByKeys(row, ['eventname', 'event_name', 'event name', 'name', 'event', 'title']),
            start_date: formatDate(findValueByKeys(row, ['startdate', 'start_date', 'start date', 'start', 'date start', 'datestart', 'from date', 'fromdate'])),
            end_date: formatDate(findValueByKeys(row, ['enddate', 'end_date', 'end date', 'end', 'date end', 'dateend', 'to date', 'todate'])),
            state: stateValue,
          };

          const category = findValueByKeys(row, ['category', 'cat', 'type']);
          if (category) event.category = category;

          const subCategory = findValueByKeys(row, ['subcategory', 'sub_category', 'sub category', 'subcat']);
          if (subCategory) event.sub_category = subCategory;

          const location = findValueByKeys(row, ['location', 'place', 'venue', 'address', 'city']);
          if (location) event.location = location;

          const campaignHook = findValueByKeys(row, ['campaignhook', 'campaign_hook', 'campaign hook', 'hook', 'angle', 'marketing hook']);
          if (campaignHook) event.campaign_hook = campaignHook;

          const description = findValueByKeys(row, ['description', 'desc', 'details', 'about', 'info', 'more about the event', 'more about event']);
          if (description) event.description = description;

          const source = findValueByKeys(row, ['source', 'reference', 'ref', 'link']);
          if (source) event.source = source;

          return event as EventData;
        });
        
        resolve(events);
      } catch (error) {
        reject(new Error(`Failed to parse Excel file: ${error}`));
      }
    };

    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };

    reader.readAsBinaryString(file);
  });
};

/**
 * Formats a date value from Excel to YYYY-MM-DD format
 */
const formatDate = (dateValue: any): string => {
  if (!dateValue) return '';

  try {
    let date: Date;

    if (typeof dateValue === 'number') {
      const excelEpoch = new Date(1900, 0, 1);
      date = new Date(excelEpoch.getTime() + (dateValue - 2) * 24 * 60 * 60 * 1000);
    } 
    else if (dateValue instanceof Date) {
      date = dateValue;
    } 
    else if (typeof dateValue === 'string') {
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
        return dateValue;
      }
      date = new Date(dateValue);
    } 
    else {
      return '';
    }

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    
    return `${year}-${month}-${day}`;
  } catch (error) {
    console.error('Error formatting date:', error);
    return '';
  }
};

/**
 * Validates event data
 */
export const validateEventData = (events: EventData[]): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];

  events.forEach((event, index) => {
    const rowNum = index + 2;

    if (!event.event_name) {
      errors.push(`Row ${rowNum}: Event Name is required`);
    }

    if (!event.start_date) {
      errors.push(`Row ${rowNum}: Start Date is required`);
    }

    if (!event.end_date) {
      errors.push(`Row ${rowNum}: End Date is required`);
    }

    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (event.start_date && !dateRegex.test(event.start_date)) {
      errors.push(`Row ${rowNum}: Start Date must be in YYYY-MM-DD format`);
    }

    if (event.end_date && !dateRegex.test(event.end_date)) {
      errors.push(`Row ${rowNum}: End Date must be in YYYY-MM-DD format`);
    }

    if (event.start_date && event.end_date) {
      const startDate = new Date(event.start_date);
      const endDate = new Date(event.end_date);
      
      if (startDate > endDate) {
        errors.push(`Row ${rowNum}: Start Date must be before or equal to End Date`);
      }
    }
  });

  return {
    isValid: errors.length === 0,
    errors
  };
};
